// src/components/invoices/InvoiceForm.js
import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Alert,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
  Autocomplete,
  Tooltip,
  Dialog,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  FormControlLabel,
  Chip,
} from "@mui/material";
import {
  Close,
  Save,
  Add,
  Delete,
  AddShoppingCart,
  Refresh,
  CallSplit,
} from "@mui/icons-material";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import { useCollection } from "../../hooks/useCollection";
import {
  createInvoiceLine,
  createLiquidationProjectLine,
  createLiquidationSwitchLine,
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  INVOICE_TYPE,
  INVOICE_TYPE_LABELS,
  LIQUIDATION_MODE,
} from "../../utils/models/invoiceModel";
import {
  createPurchaseInvoice,
  EMITTER_TYPES,
} from "../../utils/models/purchaseInvoiceModel";
import { CLIENT_TAGS } from "../../utils/models/clientModel";
import ClientForm from "../clients/ClientForm";
import CommercialForm from "../commercials/CommercialForm";

const QUARTER_OPTIONS = ["Q1", "Q2", "Q3", "Q4"];

const getSequenceKey = (invoiceType) => {
  if (invoiceType === INVOICE_TYPE.ABONO) return "abono";
  if (invoiceType === INVOICE_TYPE.LIQUIDACION) return "liquidacion";
  return "factura"; // normal + proforma comparten
};

const extractNumberFromInvoiceNumber = (raw) => {
  if (!raw) return null;
  const str = String(raw).trim();
  if (str === "" || str === "BORRADOR") return null;

  const withoutPrefix = str.replace(/^[A-Z]+\//i, "");
  const withoutYear = withoutPrefix.replace(/^\d{4}\//, "");
  const match = withoutYear.match(/(\d+)/);
  if (!match) return null;
  const num = parseInt(match[1], 10);
  return isNaN(num) ? null : num;
};

const computeNextNumberForSequence = (invoices, sequenceKey) => {
  if (!invoices || invoices.length === 0) return "0001";

  let maxNumber = 0;

  invoices.forEach((inv) => {
    if (inv.isActive === false) return;
    const key = getSequenceKey(inv.invoiceType);
    if (key !== sequenceKey) return;

    const n = extractNumberFromInvoiceNumber(inv.invoiceNumber);
    if (n !== null && n > maxNumber) maxNumber = n;
  });

  const next = maxNumber + 1;
  return String(next).padStart(4, "0");
};

// Devuelve nombre del cliente de cambio de comercializadora
const getSwitchClientName = (c) => {
  if (!c) return "";
  if (c.type === "company" && c.companyName) return c.companyName;
  return (
    `${c?.firstName || ""} ${c?.lastName || ""}`.trim() ||
    c?.companyName ||
    ""
  );
};

// Devuelve lista de direcciones (CUPS) del cliente de cambio de comercializadora
const getSwitchClientCupsOptions = (client) => {
  if (!client) return [];
  const addresses = Array.isArray(client.addresses) ? client.addresses : [];

  // Fallback: si no hay addresses pero sí cups/cupsGas en raíz
  const fallback = [];
  if (addresses.length === 0) {
    if (client.cups || client.cupsGas) {
      fallback.push({
        street: client.address?.street || "",
        city: client.address?.city || "",
        cups: client.cups || "",
        cupsGas: client.cupsGas || "",
      });
    }
  }

  const list = addresses.length > 0 ? addresses : fallback;

  return list
    .map((a, idx) => {
      const cups = (a.cups || "").trim();
      const cupsGas = (a.cupsGas || "").trim();
      const cupsValue = cups || cupsGas;
      if (!cupsValue) return null;
      const street = a.street || "";
      const city = a.city || "";
      return {
        value: cupsValue,
        cups: cupsValue,
        street,
        city,
        label: `${cupsValue}${street ? ` — ${street}` : ""}${
          city ? `, ${city}` : ""
        }`,
        index: idx,
      };
    })
    .filter(Boolean);
};

// Devuelve la fecha de entrada del trámite ACTIVO (sin fechaSalida)
// que coincide con el cliente y el CUPS indicados.
const findActiveSwitchEntryDate = (switches, clientId, cups) => {
  if (!switches || !clientId || !cups) return "";

  const cupsNorm = String(cups).trim().toLowerCase();

  const matches = switches.filter((s) => {
    if (!s || s.isActive === false) return false;
    if (String(s.switchClientId || "") !== String(clientId)) return false;
    if (s.fechaSalida) return false; // solo el activo

    const clientCups = String(s.addressCups || "").trim().toLowerCase();
    const clientCupsGas = String(s.addressCupsGas || "").trim().toLowerCase();

    return clientCups === cupsNorm || clientCupsGas === cupsNorm;
  });

  if (matches.length === 0) return "";

  // Si hay más de uno (raro), nos quedamos con el más reciente por fechaEntrada
  matches.sort((a, b) => {
    const da = a.fechaEntrada ? new Date(a.fechaEntrada).getTime() : 0;
    const db = b.fechaEntrada ? new Date(b.fechaEntrada).getTime() : 0;
    return db - da;
  });

  const chosen = matches[0];
  if (!chosen.fechaEntrada) return "";

  // Normalizamos a "YYYY-MM-DD" (lo que espera el <input type="date">)
  try {
    return new Date(chosen.fechaEntrada).toISOString().split("T")[0];
  } catch {
    return "";
  }
};

const InvoiceForm = ({ invoice, preset, onSave, onCancel }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { add: addInvoice, update: updateInvoice } =
    useAuditFirestore("invoices");
  const { add: addSalesInvoice } = useAuditFirestore("salesInvoices");
  const { add: addPurchaseInvoice } = useAuditFirestore("purchaseInvoices");
  const { data: clients, refresh: refreshClients } = useCollection("clients");
  const { data: commercials, refresh: refreshCommercials } =
    useCollection("commercials");
  const { data: projects } = useCollection("projects");
  const { data: allInvoices } = useCollection("invoices");
  const { data: switches } = useCollection("switches");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [commercialDialogOpen, setCommercialDialogOpen] = useState(false);
  const [manualTotal, setManualTotal] = useState(false);

  // Preset de fracción
  const isPresetFraction = !!preset?.isFractioned;
  const baseInvoice = preset?.baseInvoice || null;

  const initialType =
    invoice?.invoiceType || preset?.invoiceType || INVOICE_TYPE.NORMAL;

  const [autoNumber, setAutoNumber] = useState(!isPresetFraction);

  const [isFractioned, setIsFractioned] = useState(
    isPresetFraction ||
      !!(
        invoice?.invoiceType === INVOICE_TYPE.PROFORMA &&
        invoice?.fraction === "A"
      ),
  );

  // Modo de liquidación: proyecto o cambio de comercializadora
  const [liquidationMode, setLiquidationMode] = useState(
    invoice?.liquidationMode ||
      (invoice?.invoiceType === INVOICE_TYPE.LIQUIDACION
        ? LIQUIDATION_MODE.PROJECT
        : LIQUIDATION_MODE.PROJECT),
  );

  const [formData, setFormData] = useState({
    invoiceType: initialType,
    liquidationMode:
      invoice?.liquidationMode ||
      (initialType === INVOICE_TYPE.LIQUIDACION
        ? LIQUIDATION_MODE.PROJECT
        : ""),
    invoiceNumber:
      invoice?.invoiceNumber ||
      baseInvoice?.invoiceNumber ||
      preset?.proformaNumber ||
      "",
    proformaNumber:
      invoice?.proformaNumber || preset?.proformaNumber || "",
    fraction: invoice?.fraction || preset?.fraction || "",

    clientId: invoice?.clientId || baseInvoice?.clientId || "",
    clientName: invoice?.clientName || baseInvoice?.clientName || "",
    clientTaxId: invoice?.clientTaxId || baseInvoice?.clientTaxId || "",
    clientEmail: invoice?.clientEmail || baseInvoice?.clientEmail || "",
    clientAddress: invoice?.clientAddress || baseInvoice?.clientAddress || "",

    commercialId: invoice?.commercialId || baseInvoice?.commercialId || "",
    commercialName:
      invoice?.commercialName || baseInvoice?.commercialName || "",
    commercialTaxId:
      invoice?.commercialTaxId || baseInvoice?.commercialTaxId || "",
    commercialEmail:
      invoice?.commercialEmail || baseInvoice?.commercialEmail || "",
    commercialAddress:
      invoice?.commercialAddress || baseInvoice?.commercialAddress || "",

    commercialCompanyName:
      invoice?.commercialCompanyName ||
      baseInvoice?.commercialCompanyName ||
      "",
    commercialCompanyTaxId:
      invoice?.commercialCompanyTaxId ||
      baseInvoice?.commercialCompanyTaxId ||
      "",
    commercialCompanyAddress:
      invoice?.commercialCompanyAddress ||
      baseInvoice?.commercialCompanyAddress ||
      "",
    assignToCompany:
      invoice?.assignToCompany || baseInvoice?.assignToCompany || false,

    issuerName:
      invoice?.issuerName ||
      baseInvoice?.issuerName ||
      "EPC Development & Management, S.L.",
    issuerTaxId:
      invoice?.issuerTaxId || baseInvoice?.issuerTaxId || "B26623850",
    issuerAddress:
      invoice?.issuerAddress ||
      baseInvoice?.issuerAddress ||
      "C/ Juan de Austria 13, Cox, Alicante",

    lines: invoice?.lines || [],

    baseAmount: invoice?.baseAmount || 0,
    taxRate: invoice?.taxRate ?? baseInvoice?.taxRate ?? 21,
    taxAmount: invoice?.taxAmount || 0,
    total: invoice?.total || 0,

    issueDate:
      invoice?.issueDate?.split("T")[0] ||
      (baseInvoice?.issueDate
        ? new Date(baseInvoice.issueDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0]),
    dueDate:
      invoice?.dueDate?.split("T")[0] ||
      (baseInvoice?.dueDate
        ? new Date(baseInvoice.dueDate).toISOString().split("T")[0]
        : ""),
    paymentMethod:
      invoice?.paymentMethod ||
      baseInvoice?.paymentMethod ||
      PAYMENT_METHODS.TRANSFER,
    iban:
      invoice?.iban ||
      baseInvoice?.iban ||
      "ES31 0182 4428 1602 0155 9169",
    status: invoice?.status || "unpaid",
    notes: invoice?.notes || baseInvoice?.notes || "",

    salesInvoiceId: null,
    purchaseInvoiceId: null,
    relatedInvoiceId: null,
    relatedInvoiceNumber: null,
    convertedToInvoiceId: null,
    convertedAt: null,
  });

  const getClientName = (c) =>
    c?.companyName ||
    `${c?.firstName || ""} ${c?.lastName || ""}`.trim() ||
    "";

  const getCommercialName = (c) =>
    `${c?.firstName || ""} ${c?.lastName || ""}`.trim() || "";

  const clientList = React.useMemo(() => {
    const list = (clients || []).filter((c) => c.isActive !== false);
    return [...list].sort((a, b) =>
      getClientName(a).localeCompare(getClientName(b), "es", {
        sensitivity: "base",
      }),
    );
  }, [clients]);

  const commercialList = React.useMemo(() => {
    const list = (commercials || []).filter((c) => c.isActive !== false);
    return [...list].sort((a, b) =>
      getCommercialName(a).localeCompare(getCommercialName(b), "es", {
        sensitivity: "base",
      }),
    );
  }, [commercials]);

  // Lista de clientes de cambio de comercializadora
  // (son clientes de la colección "clients" con el tag CAMBIO_COMERCIALIZADORA)
  const switchClientList = React.useMemo(() => {
    const list = (clients || []).filter(
      (c) =>
        c.isActive !== false &&
        c.tags?.includes(CLIENT_TAGS.CAMBIO_COMERCIALIZADORA),
    );
    return [...list].sort((a, b) =>
      getSwitchClientName(a).localeCompare(getSwitchClientName(b), "es", {
        sensitivity: "base",
      }),
    );
  }, [clients]);

  const activeProjects = projects?.filter((p) => p.isActive !== false) || [];

  const isLiquidacion = formData.invoiceType === INVOICE_TYPE.LIQUIDACION;
  const isAbono = formData.invoiceType === INVOICE_TYPE.ABONO;
  const isProforma = formData.invoiceType === INVOICE_TYPE.PROFORMA;

  const isLiquidationProject =
    isLiquidacion && liquidationMode === LIQUIDATION_MODE.PROJECT;
  const isLiquidationSwitch =
    isLiquidacion && liquidationMode === LIQUIDATION_MODE.SWITCH;

  // Autonumeración (solo si no viene preset de fracción ni estamos editando)
  useEffect(() => {
    if (invoice?.id) return;
    if (isPresetFraction) return;
    if (!allInvoices) return;
    if (!autoNumber) return;

    const seqKey = getSequenceKey(formData.invoiceType);
    const nextNumber = computeNextNumberForSequence(allInvoices, seqKey);

    setFormData((prev) => {
      if (prev.invoiceNumber === nextNumber) return prev;
      return { ...prev, invoiceNumber: nextNumber };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allInvoices, formData.invoiceType, autoNumber, invoice?.id, isPresetFraction]);

  // Recalcular totales
  useEffect(() => {
    if (manualTotal) return;

    const lines = formData.lines || [];

    if (formData.invoiceType === INVOICE_TYPE.LIQUIDACION) {
      const baseAmount = lines.reduce(
        (sum, l) => sum + (parseFloat(l.commission) || 0),
        0,
      );

      setFormData((prev) => ({
        ...prev,
        baseAmount,
        taxRate: 0,
        taxAmount: 0,
        total: baseAmount,
      }));
      return;
    }

    const baseAmount = lines.reduce(
      (sum, line) => sum + (parseFloat(line.total) || 0),
      0,
    );
    const taxAmount = lines.reduce(
      (sum, line) => sum + (parseFloat(line.taxAmount) || 0),
      0,
    );
    const total = baseAmount + taxAmount;
    const avgTaxRate = baseAmount > 0 ? (taxAmount / baseAmount) * 100 : 21;

    setFormData((prev) => ({
      ...prev,
      baseAmount,
      taxAmount,
      total,
      taxRate: Math.round(avgTaxRate * 100) / 100,
    }));
  }, [formData.lines, formData.invoiceType, manualTotal]);

  const handleTypeChange = (event, newType) => {
    if (newType === null) return;

    setFormData((prev) => {
      const baseUpdate = { ...prev, invoiceType: newType };

      if (
        newType === INVOICE_TYPE.LIQUIDACION &&
        prev.invoiceType !== INVOICE_TYPE.LIQUIDACION
      ) {
        const newLines = (prev.lines || []).map((l) =>
          createLiquidationProjectLine({
            id: l.id,
            projectId: l.projectId || null,
            projectName: l.projectName || "",
            quarter: "Q1",
            commission: l.total || 0,
          }),
        );
        return {
          ...baseUpdate,
          liquidationMode: LIQUIDATION_MODE.PROJECT,
          lines: newLines,
          clientId: "",
          clientName: "",
          clientTaxId: "",
          clientEmail: "",
          clientAddress: "",
          paymentMethod: "",
          iban: "",
          fraction: "",
          proformaNumber: "",
        };
      }

      if (
        prev.invoiceType === INVOICE_TYPE.LIQUIDACION &&
        newType !== INVOICE_TYPE.LIQUIDACION
      ) {
        const newLines = (prev.lines || []).map((l) => ({
          id: l.id,
          description: "",
          quantity: 1,
          unitPrice: parseFloat(l.commission) || 0,
          taxRate: 21,
          total: parseFloat(l.commission) || 0,
          taxAmount: (parseFloat(l.commission) || 0) * 0.21,
          projectId: l.projectId || null,
        }));
        return {
          ...baseUpdate,
          liquidationMode: "",
          lines: newLines,
          commercialId: "",
          commercialName: "",
          commercialTaxId: "",
          commercialEmail: "",
          commercialAddress: "",
          commercialCompanyName: "",
          commercialCompanyTaxId: "",
          commercialCompanyAddress: "",
          assignToCompany: false,
          paymentMethod: PAYMENT_METHODS.TRANSFER,
          iban: "ES31 0182 4428 1602 0155 9169",
        };
      }

      if (newType !== INVOICE_TYPE.PROFORMA) {
        baseUpdate.fraction = "";
        baseUpdate.proformaNumber = "";
      }

      return baseUpdate;
    });

    if (newType !== INVOICE_TYPE.PROFORMA) {
      setIsFractioned(false);
    }

    if (newType !== INVOICE_TYPE.LIQUIDACION) {
      setLiquidationMode(LIQUIDATION_MODE.PROJECT);
    }

    if (!invoice?.id && !isPresetFraction) setAutoNumber(true);

    if (error) setError("");
  };

  // Cambio de modo de liquidación (proyecto <-> cambio de comercializadora)
  const handleLiquidationModeChange = (event, newMode) => {
    if (newMode === null) return;

    setLiquidationMode(newMode);

    setFormData((prev) => {
      // Limpiar líneas al cambiar de modo para evitar mezclar estructuras
      return {
        ...prev,
        liquidationMode: newMode,
        lines: [],
        baseAmount: 0,
        taxRate: 0,
        taxAmount: 0,
        total: 0,
      };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "invoiceNumber") {
      setAutoNumber(false);
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
  };

  const handleClientSelect = (selectedClient) => {
    if (!selectedClient) {
      setFormData((prev) => ({
        ...prev,
        clientId: "",
        clientName: "",
        clientTaxId: "",
        clientEmail: "",
        clientAddress: "",
      }));
      return;
    }
    const name =
      selectedClient.companyName ||
      `${selectedClient.firstName || ""} ${selectedClient.lastName || ""}`.trim();
    const taxId =
      selectedClient.taxId ||
      selectedClient.companyTaxId ||
      selectedClient.dni ||
      "";
    const email = selectedClient.email || "";
    const address = selectedClient.address?.street
      ? `${selectedClient.address.street}, ${selectedClient.address.city}`
      : "";

    setFormData((prev) => ({
      ...prev,
      clientId: selectedClient.id,
      clientName: name,
      clientTaxId: taxId,
      clientEmail: email,
      clientAddress: address,
    }));
  };

  const handleCommercialSelect = (selectedCommercial) => {
    if (!selectedCommercial) {
      setFormData((prev) => ({
        ...prev,
        commercialId: "",
        commercialName: "",
        commercialTaxId: "",
        commercialEmail: "",
        commercialAddress: "",
        commercialCompanyName: "",
        commercialCompanyTaxId: "",
        commercialCompanyAddress: "",
      }));
      return;
    }

    const name = `${selectedCommercial.firstName || ""} ${
      selectedCommercial.lastName || ""
    }`.trim();
    const taxId = selectedCommercial.dni || "";
    const email = selectedCommercial.email || "";
    const address = selectedCommercial.companyAddress?.street
      ? `${selectedCommercial.companyAddress.street}, ${selectedCommercial.companyAddress.city}`
      : "";

    const companyName = selectedCommercial.companyName || "";
    const companyTaxId = selectedCommercial.companyTaxId || "";
    const companyAddress = selectedCommercial.companyAddress?.street
      ? `${selectedCommercial.companyAddress.street}, ${selectedCommercial.companyAddress.city}`
      : "";

    setFormData((prev) => ({
      ...prev,
      commercialId: selectedCommercial.id,
      commercialName: name,
      commercialTaxId: taxId,
      commercialEmail: email,
      commercialAddress: address,
      commercialCompanyName: companyName,
      commercialCompanyTaxId: companyTaxId,
      commercialCompanyAddress: companyAddress,
    }));
  };

  const handleAssignToCompanyChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      assignToCompany: e.target.checked,
    }));
  };

  const handleAddLine = () => {
    if (isLiquidationSwitch) {
      const newLine = createLiquidationSwitchLine({
        switchClientId: "",
        switchClientName: "",
        cups: "",
        fechaEntrada: "",
        commission: 0,
      });
      setFormData((prev) => ({ ...prev, lines: [...prev.lines, newLine] }));
      return;
    }

    if (isLiquidationProject) {
      const newLine = createLiquidationProjectLine({
        projectId: null,
        projectName: "",
        quarter: "Q1",
        commission: 0,
      });
      setFormData((prev) => ({ ...prev, lines: [...prev.lines, newLine] }));
      return;
    }

    const newLine = createInvoiceLine({
      id: `line_${Date.now()}`,
      description: "",
      quantity: "",
      unitPrice: "",
      taxRate: 21,
      total: 0,
      taxAmount: 0,
    });
    setFormData((prev) => ({ ...prev, lines: [...prev.lines, newLine] }));
  };

  const handleLineChange = (lineId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      lines: prev.lines.map((line) => {
        if (line.id !== lineId) return line;

        const updatedLine = { ...line, [field]: value };

        if (["quantity", "unitPrice", "taxRate"].includes(field)) {
          const qty = field === "quantity" ? value : line.quantity;
          const price = field === "unitPrice" ? value : line.unitPrice;
          const rate = field === "taxRate" ? value : line.taxRate;

          const qtyNum = parseFloat(qty);
          const priceNum = parseFloat(price);
          const rateNum = parseFloat(rate) || 0;

          if (!isNaN(qtyNum) && !isNaN(priceNum)) {
            updatedLine.total = qtyNum * priceNum;
            updatedLine.taxAmount = updatedLine.total * (rateNum / 100);
            updatedLine.quantity = qtyNum;
            updatedLine.unitPrice = priceNum;
            updatedLine.taxRate = rateNum;
          } else {
            updatedLine.total = 0;
            updatedLine.taxAmount = 0;
          }
        }

        return updatedLine;
      }),
    }));
  };

  const handleLiquidationProjectLineChange = (lineId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      lines: prev.lines.map((line) => {
        if (line.id !== lineId) return line;
        const updatedLine = { ...line, [field]: value };

        if (field === "commission") {
          const num = parseFloat(value);
          updatedLine.commission = isNaN(num) ? 0 : num;
        }

        if (field === "projectId") {
          const proj = activeProjects.find((p) => p.id === value);
          updatedLine.projectName = proj?.title || proj?.name || "";
        }

        return updatedLine;
      }),
    }));
  };

  const handleLiquidationSwitchLineChange = (lineId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      lines: prev.lines.map((line) => {
        if (line.id !== lineId) return line;
        const updatedLine = { ...line, [field]: value };

        if (field === "commission") {
          const num = parseFloat(value);
          updatedLine.commission = isNaN(num) ? 0 : num;
        }

        if (field === "switchClientId") {
          const client = switchClientList.find((c) => c.id === value);
          updatedLine.switchClientName = client
            ? getSwitchClientName(client)
            : "";
          // Al cambiar de cliente, reseteamos CUPS y fecha
          updatedLine.cups = "";
          updatedLine.fechaEntrada = "";
        }

        if (field === "cups") {
          // Al seleccionar CUPS, autorrellenar fecha de entrada del trámite activo
          const autoFecha = findActiveSwitchEntryDate(
            switches,
            updatedLine.switchClientId,
            value,
          );
          if (autoFecha) {
            updatedLine.fechaEntrada = autoFecha;
          }
          // Si no hay trámite activo, no tocamos lo que hubiera
        }

        return updatedLine;
      }),
    }));
  };

  const handleRemoveLine = (lineId) => {
    setFormData((prev) => ({
      ...prev,
      lines: prev.lines.filter((l) => l.id !== lineId),
    }));
  };

  const handleManualTotalChange = (value) => {
    const val = parseFloat(value);
    setManualTotal(true);
    setFormData((prev) => ({ ...prev, total: isNaN(val) ? 0 : val }));
  };

  const handleRecalculate = () => setManualTotal(false);

  const handleToggleFraction = () => {
    setIsFractioned((prev) => !prev);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.invoiceNumber) {
      setError("El número de factura es obligatorio");
      return;
    }

    if (isLiquidacion) {
      if (!formData.commercialId) {
        setError("Selecciona un comercial");
        return;
      }
    } else {
      if (!formData.clientId) {
        setError("Selecciona un cliente");
        return;
      }
    }

    if (!formData.lines || formData.lines.length === 0) {
      setError("Añade al menos una línea");
      return;
    }

    // Validaciones específicas para liquidación con cambio de comercializadora
    if (isLiquidationSwitch) {
      const invalid = formData.lines.some(
        (l) => !l.switchClientId || !l.cups || !l.fechaEntrada,
      );
      if (invalid) {
        setError(
          "Cada línea de cambio de comercializadora debe tener cliente, CUPS y fecha de entrada",
        );
        return;
      }
    }

    setLoading(true);
    setError("");

    const isOnlyInvoices = isProforma || isLiquidacion;

    try {
      // Para proformas:
      //  - Si viene preset con fracción concreta (B, C, ...) → esa fracción
      //  - Si el usuario marcó "fraccionar" → fraction "A"
      //  - Si no → fraction ""
      const proformaNumber = isProforma
        ? preset?.proformaNumber || formData.invoiceNumber
        : "";

      let fraction = "";
      if (isProforma) {
        if (preset?.fraction) {
          fraction = preset.fraction;
        } else if (isFractioned) {
          fraction = "A";
        } else {
          fraction = "";
        }
      }

      const finalLiquidationMode = isLiquidacion ? liquidationMode : "";

      const invoiceData = {
        ...formData,
        liquidationMode: finalLiquidationMode,
        proformaNumber,
        fraction,
        iban: isAbono || isLiquidacion ? "" : formData.iban,
        paymentMethod: isLiquidacion ? "" : formData.paymentMethod,
        updatedAt: new Date().toISOString(),
      };

      let savedInvoiceId;

      if (invoice?.id) {
        await updateInvoice(invoice.id, invoiceData);
        savedInvoiceId = invoice.id;
      } else {
        const newInvoice = await addInvoice({
          ...invoiceData,
          createdAt: new Date().toISOString(),
          createdBy: "",
          isActive: true,
        });
        savedInvoiceId = newInvoice?.id;

        if (savedInvoiceId && isAbono) {
          const purchaseInvoiceData = createPurchaseInvoice({
            invoiceNumber: formData.invoiceNumber,
            emitterType: EMITTER_TYPES.CLIENT,
            emitterId: formData.clientId,
            emitterName: formData.clientName,
            emitterTaxId: formData.clientTaxId,
            emitterAddress: formData.clientAddress,
            emitterEmail: formData.clientEmail,
            receiverName: "EPC Development & Management, S.L.",
            receiverTaxId: "B26623850",
            receiverAddress: "C/ Juan de Austria 13, Cox, Alicante",
            baseAmount: formData.baseAmount,
            taxRate: formData.taxRate,
            taxAmount: formData.taxAmount,
            total: formData.total,
            lines: formData.lines,
            projects: formData.lines
              .filter((l) => l.projectId)
              .map((l) => ({
                projectId: l.projectId,
                amount: (l.total || 0) + (l.taxAmount || 0),
              })),
            issueDate: new Date(formData.issueDate).toISOString(),
            dueDate: formData.dueDate
              ? new Date(formData.dueDate).toISOString()
              : "",
            paymentMethod: formData.paymentMethod,
            iban: "",
            status: "unpaid",
            notes: formData.notes,
            relatedInvoiceId: savedInvoiceId || null,
            relatedInvoiceNumber: formData.invoiceNumber || null,
            createdAt: new Date().toISOString(),
            createdBy: "",
            isActive: true,
          });

          delete purchaseInvoiceData.id;

          const savedPurchaseInvoice =
            await addPurchaseInvoice(purchaseInvoiceData);

          await updateInvoice(savedInvoiceId, {
            purchaseInvoiceId: savedPurchaseInvoice?.id,
          });
        } else if (savedInvoiceId && !isOnlyInvoices && !isAbono) {
          const salesInvoiceData = {
            invoiceNumber: formData.invoiceNumber,
            clientId: formData.clientId,
            clientName: formData.clientName,
            clientTaxId: formData.clientTaxId,
            clientEmail: formData.clientEmail,
            clientAddress: formData.clientAddress,
            issuerName: "EPC Development & Management, S.L.",
            issuerTaxId: "B26623850",
            issuerAddress: "C/ Juan de Austria 13, Cox, Alicante",
            baseAmount: formData.baseAmount,
            taxRate: formData.taxRate,
            taxAmount: formData.taxAmount,
            total: formData.total,
            projects: formData.lines
              .filter((l) => l.projectId)
              .map((l) => ({
                projectId: l.projectId,
                amount: l.total + l.taxAmount,
              })),
            issueDate: new Date(formData.issueDate).toISOString(),
            dueDate: formData.dueDate
              ? new Date(formData.dueDate).toISOString()
              : "",
            paymentMethod: formData.paymentMethod,
            iban: formData.iban,
            status: "unpaid",
            notes: formData.notes,
            createdAt: new Date().toISOString(),
            createdBy: "",
            isActive: true,
          };

          const savedSalesInvoice = await addSalesInvoice(salesInvoiceData);

          await updateInvoice(savedInvoiceId, {
            salesInvoiceId: savedSalesInvoice?.id,
          });
        }
      }

      onSave();
    } catch (err) {
      console.error(err);
      setError(err.message || "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.background.paper,
      height: { xs: 40, sm: 44 },
    },
    "& .MuiInputLabel-root": {
      backgroundColor: theme.palette.background.paper,
      paddingInline: "4px",
      fontSize: { xs: "0.8rem", sm: "0.875rem" },
    },
  };

  const sectionTitleSx = {
    fontWeight: 700,
    fontSize: { xs: 14, sm: 15 },
    mb: { xs: 1.5, sm: 2 },
    mt: { xs: 4, sm: 5 },
    color: theme.palette.primary.main,
  };

  const getSectionTitlePrefix = () => {
    if (isProforma) return "Datos de la factura proforma";
    if (isLiquidacion) {
      return isLiquidationSwitch
        ? "Datos de la liquidación"
        : "Datos de la liquidación";
    }
    if (isAbono) return "Datos del abono";
    return "Datos de la factura";
  };

  const getInvoiceNumberLabel = () => {
    if (isProforma) return "Nº Proforma";
    if (isLiquidacion) return "Nº Liquidación";
    if (isAbono) return "Nº Abono";
    return "Nº Factura";
  };

  const getLinesTitle = () => {
    if (isAbono) return "Líneas del abono";
    if (isLiquidationSwitch) return "Líneas de la liquidación";
    if (isLiquidacion) return "Líneas de la liquidación";
    return "Líneas de factura";
  };

  const canSplitProforma = isProforma;

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: { xs: "16px", sm: "24px" },
          border: "1px solid",
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.12)"
              : "#e2e8f0",
          background: theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: { xs: 3, sm: 4 },
          }}
        >
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{ fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" } }}
          >
            {invoice?.id
              ? isProforma
                ? "Editar factura proforma"
                : isLiquidacion
                  ? isLiquidationSwitch
                    ? "Editar liquidación"
                    : "Editar liquidación"
                  : isAbono
                    ? "Editar abono"
                    : "Editar factura de venta"
              : preset?.isFractioned
                ? `Nueva fracción ${preset.fraction} de la proforma ${preset.proformaNumber}`
                : isProforma
                  ? "Nueva factura proforma"
                  : isLiquidacion
                    ? isLiquidationSwitch
                      ? "Nueva liquidación"
                      : "Nueva liquidación"
                    : isAbono
                      ? "Nuevo abono"
                      : "Nueva factura de venta"}
          </Typography>
          <IconButton
            onClick={onCancel}
            size="small"
            sx={{
              background: theme.palette.background.paper,
              border: "1px solid",
              borderColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.12)"
                  : "#e2e8f0",
            }}
          >
            <Close sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: { xs: 2, sm: 3 },
              borderRadius: "12px",
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
            }}
          >
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {/* Selector de tipo */}
          <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
            <ToggleButtonGroup
              value={formData.invoiceType}
              exclusive
              onChange={handleTypeChange}
              aria-label="tipo de factura"
              size="small"
              disabled={isPresetFraction}
              sx={{
                gap: 1,
                flexWrap: "wrap",
                justifyContent: "center",
                "& .MuiToggleButtonGroup-grouped": {
                  borderRadius: "12px !important",
                  border: `2px solid ${theme.palette.primary.main} !important`,
                },
                "& .MuiToggleButton-root": {
                  px: 3,
                  py: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: "0.8rem", sm: "0.875rem" },
                  color: theme.palette.text.secondary,
                  "&.Mui-selected": {
                    backgroundColor: theme.palette.primary.main,
                    color: "white",
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  },
                },
              }}
            >
              <ToggleButton value={INVOICE_TYPE.NORMAL}>
                {INVOICE_TYPE_LABELS.normal}
              </ToggleButton>
              <ToggleButton value={INVOICE_TYPE.PROFORMA}>
                {INVOICE_TYPE_LABELS.proforma}
              </ToggleButton>
              <ToggleButton value={INVOICE_TYPE.LIQUIDACION}>
                {INVOICE_TYPE_LABELS.liquidacion}
              </ToggleButton>
              <ToggleButton value={INVOICE_TYPE.ABONO}>
                {INVOICE_TYPE_LABELS.abono}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Selector de modo de liquidación */}
          {isLiquidacion && (
            <Box
              sx={{
                mb: 3,
                display: "flex",
                justifyContent: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <ToggleButtonGroup
                value={liquidationMode}
                exclusive
                onChange={handleLiquidationModeChange}
                aria-label="modo de liquidación"
                size="small"
                sx={{
                  gap: 1,
                  flexWrap: "wrap",
                  justifyContent: "center",
                  "& .MuiToggleButtonGroup-grouped": {
                    borderRadius: "12px !important",
                    border: `2px solid ${theme.palette.secondary.main} !important`,
                  },
                  "& .MuiToggleButton-root": {
                    px: 3,
                    py: 0.75,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    color: theme.palette.text.secondary,
                    "&.Mui-selected": {
                      backgroundColor: theme.palette.secondary.main,
                      color: "white",
                      "&:hover": {
                        backgroundColor: theme.palette.secondary.dark,
                      },
                    },
                  },
                }}
              >
                <ToggleButton value={LIQUIDATION_MODE.PROJECT}>
                  Proyecto
                </ToggleButton>
                <ToggleButton value={LIQUIDATION_MODE.SWITCH}>
                  Cambio de comercializadora
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {/* Datos */}
          <Typography sx={sectionTitleSx}>
            {getSectionTitlePrefix()}
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2.5 },
              flexWrap: "wrap",
              width: "100%",
            }}
          >
            <TextField
              fullWidth
              size="small"
              label={getInvoiceNumberLabel()}
              name="invoiceNumber"
              value={formData.invoiceNumber}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "200px" },
              }}
              required
              disabled={isPresetFraction}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />

            {/* Campo fracción */}
            {isProforma && isFractioned && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minWidth: { xs: "100%", sm: "auto" },
                }}
              >
                <TextField
                  size="small"
                  label="Fracción"
                  value={preset?.fraction || "A"}
                  InputProps={{ readOnly: true }}
                  sx={{ ...fieldSx, width: 100 }}
                  inputProps={{
                    style: {
                      fontSize: { xs: "0.8rem", sm: "0.875rem" },
                      textAlign: "center",
                      fontWeight: 700,
                      color: theme.palette.warning.dark,
                    },
                  }}
                />
              </Box>
            )}

            <TextField
              fullWidth
              size="small"
              label="Fecha emisión"
              name="issueDate"
              type="date"
              value={formData.issueDate}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "200px" },
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Fecha vencimiento"
              name="dueDate"
              type="date"
              value={formData.dueDate}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "200px" },
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
          </Box>

          {/* Botón fraccionar (solo si NO viene preset) */}
          {canSplitProforma && !isPresetFraction && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant={isFractioned ? "contained" : "outlined"}
                color="warning"
                startIcon={<CallSplit />}
                onClick={handleToggleFraction}
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  boxShadow: isFractioned ? "none" : undefined,
                }}
              >
                {isFractioned
                  ? "Quitar fracción"
                  : "Fraccionar proforma (añadir -A)"}
              </Button>
            </Box>
          )}

          {/* Cliente o Comercial */}
          <Typography sx={sectionTitleSx}>
            {isLiquidacion
              ? "Comercial"
              : isAbono
                ? "Cliente (receptor del abono)"
                : "Cliente"}
          </Typography>

          {isLiquidacion ? (
            <>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={commercialList}
                  getOptionLabel={(option) =>
                    `${option?.firstName || ""} ${option?.lastName || ""}`.trim() ||
                    ""
                  }
                  value={
                    commercialList.find(
                      (c) => c.id === formData.commercialId,
                    ) || null
                  }
                  onChange={(_, value) => handleCommercialSelect(value)}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Seleccionar comercial"
                      required
                      sx={{ ...fieldSx }}
                    />
                  )}
                  sx={{ flex: 1 }}
                />
                <Tooltip title="Nuevo comercial">
                  <IconButton
                    onClick={() => setCommercialDialogOpen(true)}
                    sx={{
                      background: theme.palette.primary.main,
                      color: "white",
                      "&:hover": { background: theme.palette.primary.dark },
                    }}
                  >
                    <Add />
                  </IconButton>
                </Tooltip>
              </Box>

              {formData.commercialId && (
                <>
                  <Box sx={{ mt: 1.5 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!formData.assignToCompany}
                          onChange={handleAssignToCompanyChange}
                          size="small"
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
                        >
                          Asignar a empresa
                        </Typography>
                      }
                    />
                  </Box>

                  {formData.assignToCompany ? (
                    <Box
                      sx={{
                        mt: 1.5,
                        display: "flex",
                        gap: 1.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <TextField
                        size="small"
                        label="Nombre empresa"
                        value={formData.commercialCompanyName}
                        InputProps={{ readOnly: true }}
                        sx={{ ...fieldSx, flex: 1, minWidth: "220px" }}
                      />
                      <TextField
                        size="small"
                        label="CIF empresa"
                        value={formData.commercialCompanyTaxId}
                        InputProps={{ readOnly: true }}
                        sx={{ ...fieldSx, flex: 1, minWidth: "180px" }}
                      />
                      <TextField
                        size="small"
                        label="Dirección empresa"
                        value={formData.commercialCompanyAddress}
                        InputProps={{ readOnly: true }}
                        sx={{ ...fieldSx, flex: 2, minWidth: "250px" }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        mt: 1.5,
                        display: "flex",
                        gap: 1.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <TextField
                        size="small"
                        label="Nombre"
                        value={formData.commercialName}
                        InputProps={{ readOnly: true }}
                        sx={{ ...fieldSx, flex: 1, minWidth: "200px" }}
                      />
                      <TextField
                        size="small"
                        label="DNI"
                        value={formData.commercialTaxId}
                        InputProps={{ readOnly: true }}
                        sx={{ ...fieldSx, flex: 1, minWidth: "150px" }}
                      />
                      <TextField
                        size="small"
                        label="Email"
                        value={formData.commercialEmail}
                        InputProps={{ readOnly: true }}
                        sx={{ ...fieldSx, flex: 1, minWidth: "200px" }}
                      />
                      <TextField
                        size="small"
                        label="Dirección"
                        value={formData.commercialAddress}
                        InputProps={{ readOnly: true }}
                        sx={{ ...fieldSx, flex: 2, minWidth: "250px" }}
                      />
                    </Box>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={clientList}
                  getOptionLabel={(option) =>
                    option?.companyName ||
                    `${option?.firstName || ""} ${option?.lastName || ""}`.trim() ||
                    ""
                  }
                  value={
                    clientList.find((c) => c.id === formData.clientId) || null
                  }
                  onChange={(_, value) => handleClientSelect(value)}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Seleccionar cliente"
                      required
                      sx={{ ...fieldSx }}
                    />
                  )}
                  sx={{ flex: 1 }}
                />
                <Tooltip title="Nuevo cliente">
                  <IconButton
                    onClick={() => setClientDialogOpen(true)}
                    sx={{
                      background: theme.palette.primary.main,
                      color: "white",
                      "&:hover": { background: theme.palette.primary.dark },
                    }}
                  >
                    <Add />
                  </IconButton>
                </Tooltip>
              </Box>

              {formData.clientId && (
                <Box
                  sx={{
                    mt: 1.5,
                    display: "flex",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <TextField
                    size="small"
                    label="Nombre fiscal"
                    value={formData.clientName}
                    InputProps={{ readOnly: true }}
                    sx={{ ...fieldSx, flex: 1, minWidth: "200px" }}
                  />
                  <TextField
                    size="small"
                    label="CIF/NIF"
                    value={formData.clientTaxId}
                    InputProps={{ readOnly: true }}
                    sx={{ ...fieldSx, flex: 1, minWidth: "150px" }}
                  />
                  <TextField
                    size="small"
                    label="Email"
                    value={formData.clientEmail}
                    InputProps={{ readOnly: true }}
                    sx={{ ...fieldSx, flex: 1, minWidth: "200px" }}
                  />
                  <TextField
                    size="small"
                    label="Dirección"
                    value={formData.clientAddress}
                    InputProps={{ readOnly: true }}
                    sx={{ ...fieldSx, flex: 2, minWidth: "250px" }}
                  />
                </Box>
              )}
            </>
          )}

          {/* Líneas */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 4,
              mb: 2,
            }}
          >
            <Typography sx={{ ...sectionTitleSx, mt: 0, mb: 0 }}>
              {getLinesTitle()}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddShoppingCart />}
              onClick={handleAddLine}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
              }}
            >
              Añadir línea
            </Button>
          </Box>

          {formData.lines.length > 0 ? (
            <TableContainer
              sx={{
                borderRadius: "12px",
                border: "1px solid",
                borderColor: theme.palette.divider,
                mb: 2,
              }}
            >
              <Table size={isMobile ? "small" : "medium"}>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(0, 175, 163, 0.1)"
                          : "rgba(0, 95, 101, 0.05)",
                    }}
                  >
                    {isLiquidationSwitch ? (
                      <>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          Cliente
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            width: 200,
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          CUPS
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            width: 160,
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          Fecha entrada
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            width: 200,
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          Comisión sin IVA
                        </TableCell>
                        <TableCell sx={{ width: 60 }} />
                      </>
                    ) : isLiquidationProject ? (
                      <>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          Proyecto
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            width: 140,
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          Trimestre
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            width: 200,
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          Comisión sin IVA
                        </TableCell>
                        <TableCell sx={{ width: 60 }} />
                      </>
                    ) : (
                      <>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            width: "35%",
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          Concepto
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            width: "10%",
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          Cant.
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            width: "15%",
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          Precio ud.
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            width: "10%",
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          IVA %
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            width: "15%",
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          Total
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            width: "10%",
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          Proyecto
                        </TableCell>
                        <TableCell sx={{ width: "5%" }} />
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.lines.map((line) => {
                    // === Línea de cambio de comercializadora ===
                    if (isLiquidationSwitch) {
                      const selectedClient = switchClientList.find(
                        (c) => c.id === line.switchClientId,
                      );
                      const cupsOptions =
                        getSwitchClientCupsOptions(selectedClient);
                      const selectedCups =
                        cupsOptions.find((o) => o.value === line.cups) || null;

                      return (
                        <TableRow key={line.id}>
                          <TableCell>
                            <Autocomplete
                              size="small"
                              options={switchClientList}
                              getOptionLabel={(option) =>
                                getSwitchClientName(option)
                              }
                              value={selectedClient || null}
                              onChange={(_, value) =>
                                handleLiquidationSwitchLineChange(
                                  line.id,
                                  "switchClientId",
                                  value?.id || "",
                                )
                              }
                              isOptionEqualToValue={(option, value) =>
                                option.id === value?.id
                              }
                              filterOptions={(options, { inputValue }) => {
                                const q = inputValue.trim().toLowerCase();
                                if (!q) return options;
                                return options.filter((c) => {
                                  const name = getSwitchClientName(
                                    c,
                                  ).toLowerCase();
                                  const email = (c.email || "").toLowerCase();
                                  const phone = (c.phone || "").toLowerCase();
                                  const cups = (
                                    c.cups ||
                                    c.addresses?.[0]?.cups ||
                                    ""
                                  ).toLowerCase();
                                  const cupsGas = (
                                    c.cupsGas ||
                                    c.addresses?.[0]?.cupsGas ||
                                    ""
                                  ).toLowerCase();
                                  return (
                                    name.includes(q) ||
                                    email.includes(q) ||
                                    phone.includes(q) ||
                                    cups.includes(q) ||
                                    cupsGas.includes(q)
                                  );
                                });
                              }}
                              renderInput={(params) => (
                                <TextField {...params} placeholder="Cliente" />
                              )}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Autocomplete
                              size="small"
                              options={cupsOptions}
                              getOptionLabel={(option) => option.label}
                              value={selectedCups}
                              onChange={(_, value) =>
                                handleLiquidationSwitchLineChange(
                                  line.id,
                                  "cups",
                                  value?.value || "",
                                )
                              }
                              isOptionEqualToValue={(option, value) =>
                                option.value === value?.value
                              }
                              disabled={!selectedClient}
                              noOptionsText="Este cliente no tiene CUPS"
                              renderInput={(params) => (
                                <TextField {...params} placeholder="CUPS" />
                              )}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="date"
                              value={line.fechaEntrada || ""}
                              onChange={(e) =>
                                handleLiquidationSwitchLineChange(
                                  line.id,
                                  "fechaEntrada",
                                  e.target.value,
                                )
                              }
                              InputLabelProps={{ shrink: true }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  width: { xs: 130, sm: 150 },
                                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              value={line.commission ?? 0}
                              onChange={(e) =>
                                handleLiquidationSwitchLineChange(
                                  line.id,
                                  "commission",
                                  e.target.value,
                                )
                              }
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment
                                    position="end"
                                    sx={{
                                      fontSize: { xs: "0.6rem", sm: "0.7rem" },
                                    }}
                                  >
                                    €
                                  </InputAdornment>
                                ),
                              }}
                              inputProps={{
                                min: 0,
                                step: "0.01",
                                style: {
                                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                },
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  width: { xs: 120, sm: 160 },
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveLine(line.id)}
                              sx={{ color: theme.palette.error.main }}
                            >
                              <Delete sx={{ fontSize: { xs: 16, sm: 20 } }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    }

                    // === Línea de liquidación por proyecto ===
                    if (isLiquidationProject) {
                      return (
                        <TableRow key={line.id}>
                          <TableCell>
                            <Autocomplete
                              size="small"
                              options={activeProjects}
                              getOptionLabel={(option) =>
                                option?.title || option?.name || ""
                              }
                              value={
                                activeProjects.find(
                                  (p) => p.id === line.projectId,
                                ) || null
                              }
                              onChange={(_, value) =>
                                handleLiquidationProjectLineChange(
                                  line.id,
                                  "projectId",
                                  value?.id || null,
                                )
                              }
                              isOptionEqualToValue={(option, value) =>
                                option.id === value?.id
                              }
                              renderInput={(params) => (
                                <TextField {...params} placeholder="Proyecto" />
                              )}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              select
                              size="small"
                              value={line.quarter || "Q1"}
                              onChange={(e) =>
                                handleLiquidationProjectLineChange(
                                  line.id,
                                  "quarter",
                                  e.target.value,
                                )
                              }
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  width: 100,
                                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                },
                              }}
                            >
                              {QUARTER_OPTIONS.map((q) => (
                                <MenuItem key={q} value={q}>
                                  {q}
                                </MenuItem>
                              ))}
                            </TextField>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              value={line.commission ?? 0}
                              onChange={(e) =>
                                handleLiquidationProjectLineChange(
                                  line.id,
                                  "commission",
                                  e.target.value,
                                )
                              }
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment
                                    position="end"
                                    sx={{
                                      fontSize: { xs: "0.6rem", sm: "0.7rem" },
                                    }}
                                  >
                                    €
                                  </InputAdornment>
                                ),
                              }}
                              inputProps={{
                                min: 0,
                                step: "0.01",
                                style: {
                                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                },
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  width: { xs: 120, sm: 160 },
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveLine(line.id)}
                              sx={{ color: theme.palette.error.main }}
                            >
                              <Delete sx={{ fontSize: { xs: 16, sm: 20 } }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    }

                    // === Línea normal ===
                    return (
                      <TableRow key={line.id}>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Descripción"
                            value={line.description}
                            onChange={(e) =>
                              handleLineChange(
                                line.id,
                                "description",
                                e.target.value,
                              )
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                fontSize: { xs: "0.7rem", sm: "0.8rem" },
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={line.quantity}
                            onChange={(e) =>
                              handleLineChange(
                                line.id,
                                "quantity",
                                e.target.value,
                              )
                            }
                            inputProps={{
                              min: 0,
                              step: "0.01",
                              style: {
                                fontSize: { xs: "0.7rem", sm: "0.8rem" },
                              },
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                width: { xs: 60, sm: 80 },
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={line.unitPrice}
                            onChange={(e) =>
                              handleLineChange(
                                line.id,
                                "unitPrice",
                                e.target.value,
                              )
                            }
                            InputProps={{
                              endAdornment: (
                                <InputAdornment
                                  position="end"
                                  sx={{
                                    fontSize: { xs: "0.6rem", sm: "0.7rem" },
                                  }}
                                >
                                  €
                                </InputAdornment>
                              ),
                            }}
                            inputProps={{
                              min: 0,
                              step: "0.01",
                              style: {
                                fontSize: { xs: "0.7rem", sm: "0.8rem" },
                              },
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                width: { xs: 80, sm: 110 },
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={line.taxRate}
                            onChange={(e) =>
                              handleLineChange(line.id, "taxRate", e.target.value)
                            }
                            InputProps={{
                              endAdornment: (
                                <InputAdornment
                                  position="end"
                                  sx={{
                                    fontSize: { xs: "0.6rem", sm: "0.7rem" },
                                  }}
                                >
                                  %
                                </InputAdornment>
                              ),
                            }}
                            inputProps={{
                              min: 0,
                              max: 100,
                              style: {
                                fontSize: { xs: "0.7rem", sm: "0.8rem" },
                              },
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                width: { xs: 65, sm: 80 },
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            fontWeight={600}
                            sx={{ fontSize: { xs: "0.7rem", sm: "0.85rem" } }}
                          >
                            {line.total?.toFixed(2)}€
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Autocomplete
                            size="small"
                            options={activeProjects}
                            getOptionLabel={(option) =>
                              option?.title || option?.name || ""
                            }
                            value={
                              activeProjects.find(
                                (p) => p.id === line.projectId,
                              ) || null
                            }
                            onChange={(_, value) =>
                              handleLineChange(
                                line.id,
                                "projectId",
                                value?.id || null,
                              )
                            }
                            isOptionEqualToValue={(option, value) =>
                              option.id === value?.id
                            }
                            renderInput={(params) => (
                              <TextField {...params} placeholder="Proyecto" />
                            )}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                width: { xs: 100, sm: 140 },
                                fontSize: { xs: "0.7rem", sm: "0.8rem" },
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveLine(line.id)}
                            sx={{ color: theme.palette.error.main }}
                          >
                            <Delete sx={{ fontSize: { xs: 16, sm: 20 } }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                textAlign: "center",
                borderRadius: "12px",
                border: "1px dashed",
                borderColor: theme.palette.divider,
                mb: 2,
              }}
            >
              <Typography
                color="text.secondary"
                sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
              >
                No hay líneas. Haz clic en "Añadir línea" para comenzar.
              </Typography>
            </Paper>
          )}

          {/* Totales */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: "12px",
              border: "1px solid",
              borderColor: theme.palette.divider,
              background:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.03)"
                  : "#f8fafc",
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Box sx={{ textAlign: "right", minWidth: 220 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                  >
                    {isLiquidacion ? "Comisión sin IVA:" : "Base imponible:"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                  >
                    {formData.baseAmount?.toFixed(2)}€
                  </Typography>
                </Box>

                {!isLiquidacion && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                    >
                      IVA:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                    >
                      {formData.taxAmount?.toFixed(2)}€
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 1 }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}
                  >
                    Total:
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={formData.total}
                    onChange={(e) => handleManualTotalChange(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">€</InputAdornment>
                      ),
                    }}
                    inputProps={{
                      step: "0.01",
                      style: {
                        fontSize: { xs: "0.85rem", sm: "1rem" },
                        textAlign: "right",
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                      },
                    }}
                    sx={{
                      width: 150,
                      "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                    }}
                  />
                </Box>
                {manualTotal && (
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}
                  >
                    <Button
                      size="small"
                      startIcon={<Refresh sx={{ fontSize: 16 }} />}
                      onClick={handleRecalculate}
                      sx={{
                        textTransform: "none",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                    >
                      Recalcular automáticamente
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>

          {/* Forma de cobro */}
          {!isAbono && !isLiquidacion && (
            <>
              <Typography sx={sectionTitleSx}>Forma de cobro</Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  select
                  size="small"
                  label="Método"
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleChange}
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "200px" },
                  }}
                  SelectProps={{
                    MenuProps: { PaperProps: { sx: { borderRadius: "12px" } } },
                  }}
                  inputProps={{
                    style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                  }}
                >
                  {Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth
                  size="small"
                  label="IBAN"
                  name="iban"
                  value={formData.iban}
                  onChange={handleChange}
                  sx={{
                    ...fieldSx,
                    flex: 2,
                    minWidth: { xs: "100%", sm: "250px" },
                  }}
                  inputProps={{
                    style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                  }}
                />
              </Box>
            </>
          )}

          {/* Notas */}
          <Typography sx={sectionTitleSx}>Notas</Typography>
          <TextField
            fullWidth
            size="small"
            label="Notas internas"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            multiline
            rows={2}
            sx={{
              ...fieldSx,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                height: "auto",
              },
            }}
            inputProps={{
              style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
            }}
          />

          <Divider sx={{ mt: { xs: 4, sm: 5 }, mb: { xs: 2, sm: 3 } }} />

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="outlined"
              onClick={onCancel}
              sx={{
                borderRadius: "12px",
                px: { xs: 2, sm: 3 },
                height: { xs: 40, sm: 44 },
                textTransform: "none",
                fontSize: { xs: "0.8rem", sm: "0.85rem" },
              }}
              fullWidth={{ xs: true, sm: false }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={<Save />}
              sx={{
                borderRadius: "12px",
                px: { xs: 2, sm: 3 },
                height: { xs: 40, sm: 44 },
                boxShadow: "none",
                textTransform: "none",
                fontSize: { xs: "0.8rem", sm: "0.85rem" },
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                "&:hover": {
                  boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
                  filter: "brightness(1.05)",
                },
              }}
              fullWidth={{ xs: true, sm: false }}
            >
              {loading
                ? "Guardando..."
                : invoice?.id
                  ? "Actualizar"
                  : preset?.isFractioned
                    ? `Crear fracción ${preset.fraction}`
                    : isProforma
                      ? "Crear factura proforma"
                      : isLiquidationSwitch
                        ? "Crear liquidación"
                        : isLiquidacion
                          ? "Crear liquidación"
                          : isAbono
                            ? "Crear abono"
                            : "Crear factura"}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Diálogo nuevo cliente */}
      <Dialog
        open={clientDialogOpen}
        onClose={() => setClientDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: "16px", sm: "24px" },
            margin: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <ClientForm
            onSave={(newClient) => {
              setClientDialogOpen(false);
              if (newClient) {
                handleClientSelect(newClient);
                if (refreshClients) refreshClients();
              }
            }}
            onCancel={() => setClientDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Diálogo nuevo comercial */}
      <Dialog
        open={commercialDialogOpen}
        onClose={() => setCommercialDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: "16px", sm: "24px" },
            margin: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <CommercialForm
            commercial={null}
            onSave={(savedCommercial) => {
              setCommercialDialogOpen(false);
              if (savedCommercial) {
                handleCommercialSelect(savedCommercial);
                if (refreshCommercials) refreshCommercials();
              }
            }}
            onCancel={() => setCommercialDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InvoiceForm;